/**
 * Core globe renderer shell: the GlobeViewer class constructor, init, data
 * loading, and current-data-type accessors. The scene/layers/interactions
 * method groups live in sibling core/ modules and are composed onto the
 * prototype via Object.assign below. Talks to the rest of the app through the
 * window.globe seam and reads shared filter state via window.* globals.
 * Three.js stays contained to this core/ folder. Kept @ts-nocheck for now.
 */
/* eslint-disable */
// @ts-nocheck
import * as THREE from 'three'
import { LineMaterial } from 'three/addons/lines/LineMaterial.js'
import { LineSegments2 } from 'three/addons/lines/LineSegments2.js'
import { LineSegmentsGeometry } from 'three/addons/lines/LineSegmentsGeometry.js'
import { EU_MEMBER_CODES } from '../../../data/eu'
import { getDataType } from '../data-type'
import { prefersReducedMotion } from '../reduced-motion'
import { interactionMethods } from './interactions'
import { layerMethods } from './layers'
import { sceneMethods } from './scene'

    export class GlobeViewer {
      constructor(container) {
        this.container = container;
        this.scene = null;
        this.camera = null;
        this.renderer = null;
        this.globeGroup = null;
        this.animationId = null;

        // === Intro tuning ===
        this.INTRO_FADE_DURATION = 700;   // ms - canvas/sphere fade-in
        this.INTRO_STAGE_DELAY = 140;     // ms - beat between reveal stages
        this.INTRO_DOLLY_FACTOR = 1.4;    // camera starts this * resting zoom (farther)
        this.INTRO_DOLLY_DURATION = 1400; // ms - dolly push-in
        this.INTRO_OPEN_LON = 18;         // curated opening face (~Europe/Africa)
        this.INTRO_OPEN_LAT = 12;
        this.GLOBE_TILT = 0;            // deg - subtle fixed axial lean (roll); spin/drag only touch yaw/pitch
        this.BUILD_BATCH = 8;             // features built per frame (chunked build)
        this.PING_STAGGER = 28;           // ms between active-region pings
        this.PING_DURATION = 520;         // ms per region ping brighten+settle

        // Reduced-motion + repeat-visit gate a fast, fade-only intro.
        this.prefersReducedMotion = prefersReducedMotion();
        let introSeen = false;
        try { introSeen = sessionStorage.getItem('atlas-intro-seen') === '1'; } catch (e) {}
        this.introFast = this.prefersReducedMotion || introSeen;

        // Curated opening face: start on a land/data-rich hemisphere instead of
        // the empty Pacific. Mapping matches animateToPosition: rotationX = -(90+lon).
        this.rotation = { x: -(90 + this.INTRO_OPEN_LON), y: -this.INTRO_OPEN_LAT };
        this.isDragging = false;
        this.mouseDownPos = { x: 0, y: 0 };
        this.mouse = { x: 0, y: 0, lastX: 0, lastY: 0 };
        this.zoom = window.innerWidth <= 768 ? 6 : 4;
        this.needsRender = true;
        this.spinSpeed = 0.2;
        this.lastPinchDist = null;
        
        // Inertia for drag physics
        this.velocity = { x: 0, y: 0 };
        this.velocityHistory = []; // Store recent velocities for smoothing
        this.velocityHistoryMax = 5; // Number of samples to average
        this.inertiaDecay = 0.93; // How quickly velocity decays (0.9 = fast, 0.99 = slow)
        this.lastDragTime = 0;
        this.keyLightOrbit = false;
        this.keyLightOrbitSpeed = 0.1;
        this.keyLightOrbitAngle = 0;
        // Resting position of the key light. When the orbit is enabled it sweeps
        // a circle derived from this position (see scene.ts), so the two models
        // stay consistent and the light never snaps when toggled.
        this.keyLightX = 5;
        this.keyLightY = 2.5;
        this.keyLightZ = 8.5;

        // Raycasting for interactions
        this.raycaster = new THREE.Raycaster();
        // Line threshold controls how close the ray must be to a line segment to register a hit
        // 0.15 was too large (~15-20px on screen), causing hover to trigger far from borders
        // 0.02 provides a tight ~2-3px hover zone that feels accurate
        this.raycaster.params.Line.threshold = 0.02;
        this.hoveredMesh = null;
        this.clickableCountries = new Set();
        this.clickableStates = new Set();
        this.clickableCountriesDigitalIds = new Set();
        this.countryStatusMap = new Map();
        this.stateStatusMap = new Map();
        // EU member states that are only clickable via EU-level legislation (no own national entry);
        // clicking them on the globe opens the EU region drawer.
        this.euOnlyCountries = new Set();
        this.lastMousePos = null; // Track last mouse position for continuous hover detection
        this.lastHoverTime = 0; // Track when we last detected a hover for sticky behavior
        this.hoverPending = false; // Throttle: run handleHoverEffect at most once per frame (rAF)
        this.drawerOpen = false; // Track if drawer is open to pause spinning

        // Regulation level - determines country border emphasis (states are always enabled alongside countries)
        // 'platform' = countries and states both active
        // 'national' = countries active (states also remain active/clickable)
        // 'regional' = states active, countries de-emphasized
        const savedLevel = localStorage.getItem('regulationLevel');
        this.regulationLevel = savedLevel || 'platform'; // Default to platform
        
        // Border color constants
        this.HOVER_BORDER_COLOR = 0x535CFF;    // Brand blue (hover/selected)
        this.ACTIVE_BORDER_COLOR = this.HOVER_BORDER_COLOR; // Same as hover for clickable geography
        this.INACTIVE_BORDER_COLOR = 0x707070; // Grey (non-clickable borders)
        this.HOVER_FILL_COLOR = 0x1447e1;      // Blue
        this.HOVER_FILL_OPACITY = 0.5;        // Semi-transparent fill
        this.ACTIVE_FILL_COLOR = 0xB3B7FF;    // Light blueish-purple (default active fill)
        this.ACTIVE_FILL_OPACITY = 0.4;        // Default active fill opacity
        this.EUDI_FILL_OPACITY = 0.85;         // EUDI Wallets tab: status colors need to read clearly
        
        // Line width constants (in pixels - works cross-platform with Line2)
        this.DEFAULT_LINE_WIDTH = 0.75;
        this.SELECTED_LINE_WIDTH = 1.5;
        
        // Selected region tracking
        this.selectedMesh = null;
        
        // Render order constants - higher values render on top
        // Used with depthTest: false to ensure active borders render above inactive
        this.ACTIVE_RENDER_ORDER = 2;
        this.INACTIVE_RENDER_ORDER = 1;
        
        // Countries drawn from sub-region geometry (e.g. US from states): outline-only interaction, no fill. Add codes here when adding new sub-region layers.
        this.COUNTRY_CODES_DRAWN_FROM_SUBREGIONS = new Set([]);
        // Override focus [lon, lat] when centering globe (e.g. US full geometry includes Alaska/Hawaii so centroid is wrong; use continental US center).
        this.CENTER_FOCUS_OVERRIDES = new Map([
          ['US', [-98, 39]], // Continental US center
        ]);
        
        // Transition system for smooth animations
        this.transitions = new Map(); // mesh -> { color, scale, startTime, duration }
        this.TRANSITION_DURATION = 150; // ms - default transition timing

        // Style tween system for animated globe-style switches (numeric props:
        // opacities, dot size). Keyed by property name so re-triggering a switch
        // mid-animation replaces the in-flight tween rather than stacking.
        this.styleTweens = new Map(); // key -> { from, to, apply, onComplete, startTime, duration }
        this.STYLE_TRANSITION_DURATION = 600; // ms - globe-style morph timing
        this.GRID_REVEAL_DURATION = 1800; // ms - grid-line draw-on trace timing
        this.DOT_REVEAL_DURATION = 1400; // ms - per-dot staggered materialize timing

        // Globe style presets. 'default' is today's light look; 'particle' is the
        // Digital IDs mode where the dotted continents become the land surface and
        // the solid sphere + grid lines recede. Colors are intentionally unchanged.
        this.currentGlobeStyle = 'default';
        this.STYLE_DEFAULTS = {
          globeFillOpacity: 0.9,
          dotSize: 2.1, // smaller to suit the denser ~40k field
          // Dots are exclusive to Digital IDs — the per-dot reveal (uReveal) tweens
          // to 0 here so they stagger out and the layer hides entirely.
          dotReveal: 0.0,
          gridLinesOpacity: 0.5,
          gridLinesScale: 1.0, // Full elevation (grid geometry is at radius 1.08)
        };
        this.STYLE_PARTICLE = {
          globeFillOpacity: 0.15,
          dotSize: 3.2, // smaller to suit the denser ~40k field
          dotReveal: 1.0, // Dots staggered fully in for Digital IDs
          gridLinesOpacity: 0.0, // Grid is exclusive to Age Assurance — hidden here
          gridLinesScale: 0.99, // Subtle descent from elevated radius (1.08)
        };

        this.meshes = {
          globeFill: null,
          dots: null,
          countryBorders: [],
          stateBorders: [],
          countryFills: [],
          stateFills: [],
          gridLines: null,
          keyLight: null,
          fillLight: null,
          rimLight: null,
          ambientLight: null,
        };

        // Bump map texture for topography
        this.bumpTexture = null;
        this.topographyEnabled = false;
        
        // Flag to track when borders are loaded and ready
        this.bordersReady = false;

        this.init();
        this.loadLawData();
      }

      init() {
        // Initialize scene
        this.scene = new THREE.Scene();

        // Camera setup
        this.camera = new THREE.PerspectiveCamera(
          45,
          this.container.clientWidth / this.container.clientHeight,
          0.1,
          1000
        );
        this.camera.position.z = this.zoom;

        // Renderer setup
        this.renderer = new THREE.WebGLRenderer({
          antialias: true,
          alpha: true,
          preserveDrawingBuffer: true // Required for PNG export
        });
        this.renderer.setSize(this.container.clientWidth, this.container.clientHeight);
        this.renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
        this.container.insertBefore(this.renderer.domElement, this.container.firstChild);
        // Canvas starts hidden; playIntro fades it in once the globe is built and
        // warm-rendered (CSS transition lives in world-map-globe-styles.ts).
        this.renderer.domElement.style.opacity = '0';
        
        // Tooltip for region name on hover (rolodex slide on first show)
        this.tooltipEl = document.getElementById('globe-region-tooltip');
        this.tooltipTrackEl = this.tooltipEl ? this.tooltipEl.querySelector('.globe-region-tooltip__track') : null;
        this.lastTooltipName = null;
        this.lastTooltipX = null;
        this.lastTooltipY = null;
        
        // Set initial cursor
        this.renderer.domElement.style.cursor = 'grab';

        // Create globe group
        this.globeGroup = new THREE.Group();
        // Fixed axial tilt (roll). The animation loop only sets rotation.x/.y,
        // so this z lean persists through spin, drag, and inertia.
        this.globeGroup.rotation.z = (this.GLOBE_TILT * Math.PI) / 180;
        this.scene.add(this.globeGroup);

        // Create starfield
        this.createStarfield(this.globeGroup);

        // Load data and create globe
        this.loadGlobeData();

        // Start animation loop
        this.lastTime = Date.now();
        this.animate();

        // Setup event listeners
        this.setupEventListeners();
      }

      async loadLawData() {
        try {
          const [countriesRes, statesRes, digitalIdsRes] = await Promise.all([
            fetch('/api/countries'),
            fetch('/api/states'),
            fetch('/api/digital-ids/regions')
          ]);
          
          const countriesData = await countriesRes.json();
          const statesData = await statesRes.json();
          let digitalIdsData = { regions: [] };
          try {
            digitalIdsData = await digitalIdsRes.json();
          } catch (e) {}
          
          // Store countries with laws (only those with hasLaw: true). US is included for Age Assurance (national-level region); states are in clickableStates for subnational.
          countriesData.countries.forEach(country => {
            if (country.hasLaw) {
              this.clickableCountries.add(country.code.toUpperCase());
            }
            if (country.status) {
              this.countryStatusMap.set(country.code.toUpperCase(), country.status);
            }
          });

          // If EU legislation is present, track which EU member states have no own national
          // entry so their individual meshes can be hidden — the EU polygon represents them.
          if (this.clickableCountries.has('EU')) {
            EU_MEMBER_CODES.forEach(code => {
              if (!this.clickableCountries.has(code)) {
                this.euOnlyCountries.add(code);
              }
            });
          }
          
          // Store states with laws
          statesData.states.forEach(state => {
            if (state.hasLaw) {
              this.clickableStates.add(state.code.toUpperCase());
            }
            if (state.status) {
              this.stateStatusMap.set(state.code.toUpperCase(), state.status);
            }
          });
          
          // Store countries with digital ID coverage (for Digital IDs filter mode)
          if (digitalIdsData.regions && Array.isArray(digitalIdsData.regions)) {
            digitalIdsData.regions.forEach(region => {
              if (region.code) {
                this.clickableCountriesDigitalIds.add(region.code.toUpperCase());
              }
            });
          }

          // Geometry (loadGlobeData) and law data load concurrently and neither
          // awaits the other. If the borders already finished building, the
          // applyModeColors() pass in loadGlobeData ran against still-empty sets
          // and left every region inactive — reapply now that the clickable /
          // status sets are populated, and request a repaint. If borders aren't
          // ready yet, loadGlobeData's own color pass picks these up instead.
          if (this.bordersReady) {
            this.applyModeColors();
            this.needsRender = true;
          }

        } catch (error) {
          console.error('Error loading law data:', error);
        }
      }

      getCurrentDataType() {
        return getDataType();
      }

      get subregionsActive() {
        return this.getCurrentDataType() === 'age_assurance' &&
          (this.regulationLevel === 'platform' || this.regulationLevel === 'regional');
      }

    }

Object.assign(GlobeViewer.prototype, sceneMethods, layerMethods, interactionMethods)
